import { loadRemoteModule } from '@angular-architects/module-federation-runtime';
import { Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ActivateOutletEventObject } from './named-router-outlet';
import { WebComponentRegistryService } from './web-component-registry.service';
import { RouterNavigationObject, WebComponentRegistryItem } from './types';
import { WebComponentBridgeService } from './web-component-bridge.service';

@UntilDestroy()
@Component({
  selector: 'cf-shell-lib-web-component-bridge',
  templateUrl: './web-component-bridge.component.html',
})
export class WebComponentBridgeComponent implements OnChanges {
  @Input()
  public route = '';

  @Input()
  public remoteurl = '';

  @Input()
  public name = ''; // name as specified in the webpack config

  public readonly instance: WebComponentRegistryItem;

  constructor(
    private router: Router,
    private ac: ActivatedRoute,
    private registryService: WebComponentRegistryService,
    private wcBridgeService: WebComponentBridgeService,
    private el: ElementRef<HTMLElement>
  ) {
    this.instance = this.registryService.getInstance();
    WebComponentRegistryService.addDataInstanceAttribute(el.nativeElement, this.instance);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    /**
     * cf-wc-bridge component must have remoteurl, route and name properties provided!
     * Example: <cf-wc-bridge name="cf_platform" route="apps" remoteurl="{{::huiAppsCtrl.appRemoteEntry}}"></cf-wc-bridge>
     */
    if (changes.remoteurl && changes.remoteurl.firstChange && this.route && this.name) {
      this.loadRemoteModule();
    }
  }

  public onActivate(event: ActivateOutletEventObject): void {
    this.wcBridgeService.initRemoteCommunicationInterface(this.el.nativeElement, event.cmpFactory, event.component, this.name);
  }

  public onDeactivate(): void {
    this.wcBridgeService.clearSubscriptions();
  }

  private loadRemoteModule() {
    const configs = this.router.config;
    configs.push({
      path: this.instance.clientRootRoute,
      outlet: this.instance.outletName,
      data: {
        appName: this.name.replace(/_/g, '-'),
      },
      loadChildren: () =>
        loadRemoteModule({
          remoteEntry: `${this.remoteurl}remoteEntry.js`,
          remoteName: this.name,
          exposedModule: `./AppModule`,
        }).then((m) => {
          return m['AppModule'];
        }),
    });

    this.router.resetConfig(configs);
    this.registryService.add({
      instance: this.instance,
      navigate: () => this.navigateToPath(),
    });
  }

  private navigateToPath(): Promise<boolean> {
    const p: RouterNavigationObject = { outlets: {} };
    let r: string[] = [this.instance.clientRootRoute];
    if (this.route) {
      r = r.concat(...decodeURIComponent(this.route).split('/'));
    }
    p.outlets[this.instance.outletName] = r;
    return this.router.navigate([p], { relativeTo: this.ac, skipLocationChange: true });
  }
}
